import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import Icona from '../cliente/Icone';
import { errore } from '../impostazioni/comuni';

// Impostazioni → Tuttofare: il dizionario (abbreviazioni, verbi, luoghi), gli alias dei nomi, le abitudini e gli
// esempi confermati. Tutto quello che il tuttofare ha imparato si vede qui, con "dimentica" per ciascuno, e le
// voci di serie si possono spegnere. Niente servizi esterni: il classificatore gira nel nostro backend
// (backend/tuttofare_modello.py) e si riaddestra da solo ogni notte e ogni 20 esempi nuovi.
// Si innesta nella pagina delle impostazioni con una riga: <ImpostazioniTuttofare />.

const NUOVA = { voce: '', significato: '', categoria: 'abbreviazione' };
const QUANDO = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '');

const Riga = ({ testo, sotto, onDimentica, chiave }) => (
  <div data-testid={chiave}>
    <b>{testo}</b>
    <span className="descr">{sotto}</span>
    {onDimentica && <div className="azioni"><button type="button" className="btn xs rosso-vuoto" onClick={onDimentica}>Dimentica</button></div>}
  </div>
);

const ImpostazioniTuttofare = () => {
  const [dati, setDati] = useState(null);
  const [nuova, setNuova] = useState(NUOVA);
  const carica = useCallback(() => axios.get(`${API}/admin/tuttofare/sapere`).then((r) => setDati(r.data))
    .catch(() => setDati({ dizionario: [], alias: [], abitudini: [], esempi: [], modello: {}, intenti: [], categorie: [] })), []);
  useEffect(() => { carica(); }, [carica]);

  const aggiungi = async () => {
    try {
      await axios.post(`${API}/admin/tuttofare/dizionario`, nuova);
      toast.success('Aggiunta al dizionario');
      setNuova(NUOVA);
      carica();
    } catch (e) { toast.error(errore(e, 'Non aggiunta')); }
  };
  const dimentica = async (cosa, id) => {
    try { await axios.delete(`${API}/admin/tuttofare/${cosa}/${encodeURIComponent(id)}`); carica(); } catch (e) { toast.error(errore(e, 'Non tolta')); }
  };
  const riaddestra = async () => {
    try {
      const r = await axios.post(`${API}/admin/tuttofare/riaddestra`);
      toast.success(r.data.modello.addestrato ? `Riaddestrato su ${r.data.modello.esempi} esempi` : 'Non ci sono ancora abbastanza esempi');
      carica();
    } catch (e) { toast.error(errore(e, 'Non riaddestrato')); }
  };

  if (!dati) return <p className="vuoto">Carico il tuttofare…</p>;
  const m = dati.modello || {};
  const imparate = (dati.dizionario || []).filter((v) => v.origine !== 'base');
  const serie = (dati.dizionario || []).filter((v) => v.origine === 'base');
  return (
    <div className="imp-griglia" data-testid="impostazioni-tuttofare">
      <section className="card">
        <div className="ttl"><div className="i"><Icona nome="piu" /></div><h2>Dizionario del tuttofare</h2></div>
        <p className="imp-nota">Come scrivete voi: abbreviazioni («app.» = appuntamento), verbi («sentire» = telefonata),
          luoghi («da lui» = casa del cliente). Il tuttofare le impara anche da solo quando correggete un comando.</p>
        <div className="imp-campi">
          <label>Voce<input value={nuova.voce} placeholder="es. sopr." onChange={(e) => setNuova({ ...nuova, voce: e.target.value })} /></label>
          <label>Significato
            {nuova.categoria === 'intento' ? (
              <select value={nuova.significato} onChange={(e) => setNuova({ ...nuova, significato: e.target.value })}>
                <option value="">…</option>
                {(dati.intenti || []).map((i) => <option key={i.id} value={i.id}>{i.nome}</option>)}
              </select>
            ) : <input value={nuova.significato} placeholder="es. sopralluogo" onChange={(e) => setNuova({ ...nuova, significato: e.target.value })} />}
          </label>
          <label>Che cos'è
            <select value={nuova.categoria} onChange={(e) => setNuova({ ...nuova, categoria: e.target.value, significato: '' })}>
              <option value="abbreviazione">Abbreviazione (si scioglie nel testo)</option>
              <option value="intento">Verbo (dice che tipo di cosa è)</option>
              <option value="luogo">Luogo (dove si va)</option>
            </select>
          </label>
        </div>
        <div className="imp-azioni">
          <button type="button" className="btn sm p" disabled={!nuova.voce.trim() || !nuova.significato.trim()} onClick={aggiungi}>Aggiungi</button>
        </div>
        {imparate.length > 0 && (
          <div className="imp-righe">
            {imparate.map((v) => (
              <Riga key={v.id} chiave={`voce-${v.voce}`} testo={`${v.voce} = ${v.significato}`}
                sotto={`${v.categoria}${v.origine === 'imparata' ? ` · imparata il ${QUANDO(v.imparata_il)}${v.imparata_da ? ` da ${v.imparata_da}` : ''}` : ' · aggiunta a mano'}`}
                onDimentica={() => dimentica('dizionario', v.id)} />
            ))}
          </div>
        )}
        <details>
          <summary className="imp-nota">Le {serie.length} voci di serie</summary>
          <div className="imp-righe">
            {serie.map((v) => (
              <Riga key={v.id} chiave={`voce-${v.voce}`} testo={`${v.voce} = ${v.significato}`} sotto={v.categoria}
                onDimentica={v.categoria === 'regola' ? null : () => dimentica('dizionario', v.id)} />
            ))}
          </div>
        </details>
      </section>

      <section className="card">
        <div className="ttl"><div className="i b"><Icona nome="persone" /></div><h2>Come li chiamate</h2></div>
        <p className="imp-nota">I soprannomi imparati dai vostri comandi: «Lupita» per Guadalupe Martinez de Los Angeles.</p>
        {!(dati.alias || []).length && <p className="vuoto">Ancora nessuno.</p>}
        <div className="imp-righe">
          {(dati.alias || []).map((a) => (
            <Riga key={a.id} chiave={`alias-${a.alias}`} testo={`“${a.alias}” = ${a.etichetta || a.riferimento_id}`}
              sotto={`${a.tipo} · imparato il ${QUANDO(a.imparato_il)}${a.imparato_da ? ` da ${a.imparato_da}` : ''} · usato ${a.conteggio || 1} volte`}
              onDimentica={() => dimentica('alias', a.id)} />
          ))}
        </div>
      </section>

      <section className="card">
        <div className="ttl"><div className="i"><Icona nome="orologio" /></div><h2>Abitudini</h2></div>
        <p className="imp-nota">Gli orari che usate di solito, il luogo, chi accompagna: servono a precompilare l'anteprima.</p>
        {!(dati.abitudini || []).length && <p className="vuoto">Ancora nessuna.</p>}
        <div className="imp-righe">
          {(dati.abitudini || []).map((a) => (
            <Riga key={a.id} chiave={`abitudine-${a.chiave}`} testo={a.chiave}
              sotto={[a.ora && `di solito alle ${a.ora}`, a.luogo && `${a.luogo}`, a.accompagna && `con ${a.accompagna}`,
                `su ${a.conteggio || 0} volte`].filter(Boolean).join(' · ')}
              onDimentica={() => dimentica('abitudini', a.id)} />
          ))}
        </div>
      </section>

      <section className="card">
        <div className="ttl"><div className="i b"><Icona nome="documento" /></div><h2>Esempi imparati <small>{dati.quanti_esempi || 0}</small></h2></div>
        <p className="imp-nota">
          Ogni comando confermato o corretto resta qui e insegna al tuttofare. Il classificatore è in casa nostra:
          {m.libreria ? '' : ' la libreria non è installata, si va avanti con le sole regole.'}
          {m.libreria && (m.addestrato ? ` addestrato su ${m.esempi} esempi il ${QUANDO(m.addestrato_il)}.` : ` servono almeno ${m.minimi} esempi di due tipi diversi.`)}
        </p>
        <div className="imp-azioni"><button type="button" className="btn sm" onClick={riaddestra}>Riaddestra ora</button></div>
        {!(dati.esempi || []).length && <p className="vuoto">Ancora nessuno.</p>}
        <div className="imp-righe">
          {(dati.esempi || []).map((e) => (
            <Riga key={e.id} chiave={`esempio-${e.id}`} testo={e.testo}
              sotto={`${e.intento || e.azione}${e.corretto ? ' · corretto a mano' : ''}${e.autore ? ` · ${e.autore}` : ''}`}
              onDimentica={() => dimentica('esempi', e.id)} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default ImpostazioniTuttofare;
